import { useMemo } from 'react';
import type { SatelliteWebRenterUserWarningsResponseDto } from '@zg-rentals/ts-satellite-client';
import type { ExternalLinkWarningData } from '../components/modals/ExternalLinkWarningModal';
import type { PersonalContactDetailsWarningData } from '../components/modals/PersonalContactDetailsWarningModal';

// Use the API response type from ts-satellite-client
// Make fields optional to handle cases where API returns partial data
export type RawUserWarningsData = Partial<SatelliteWebRenterUserWarningsResponseDto>;

export type UseUserWarningsOptions = {
  enablePersonalContactWarning?: boolean;
  enableExternalLinkWarning?: boolean;
  userWarningsData?: RawUserWarningsData;
};

export type UseUserWarningsReturn = {
  personalContactWarningData?: PersonalContactDetailsWarningData;
  externalLinkWarningData?: ExternalLinkWarningData;
};

/**
 * Shared hook to process user warnings data
 * Transforms raw API response into component-ready data structures
 * This hook does NOT fetch data - it only processes data passed to it
 */
export const useSharedUserWarnings = ({
  enablePersonalContactWarning = false,
  enableExternalLinkWarning = false,
  userWarningsData,
}: UseUserWarningsOptions): UseUserWarningsReturn => {
  const personalContactWarningData = useMemo(() => {
    if (!enablePersonalContactWarning || !userWarningsData?.personalContactDetailsWarning) {
      return undefined;
    }

    const warning = userWarningsData.personalContactDetailsWarning;
    return {
      title: warning.disclaimerTitle,
      text: warning.disclaimerText,
      linkText: warning.link?.text,
      linkUrl: warning.link?.url,
      primaryButtonLabel: warning.primaryCta?.label,
      primaryButtonType: warning.primaryCta?.type as 'Button' | 'TextButton',
      secondaryButtonLabel: warning.secondaryCta?.label,
      secondaryButtonType: warning.secondaryCta?.type as 'Button' | 'TextButton',
    };
  }, [enablePersonalContactWarning, userWarningsData]);

  const externalLinkWarningData = useMemo(() => {
    if (!enableExternalLinkWarning || !userWarningsData?.externalLinkWarning) {
      return undefined;
    }

    const warning = userWarningsData.externalLinkWarning;
    return {
      disclaimerTitle: warning.disclaimerTitle,
      disclaimerText: warning.disclaimerText,
      primaryCta: warning.primaryCta,
      allowListedDomains: warning.allowListedDomains,
    };
  }, [enableExternalLinkWarning, userWarningsData]);

  return {
    personalContactWarningData,
    externalLinkWarningData,
  };
};
